import React, { useEffect, useState } from 'react';
import { Layout, Typography, Table, Tag, Progress, message, Space, Button, Modal, Spin } from 'antd';
import { ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { doc, getDoc, collection, query, where, getCountFromServer, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import dayjs from 'dayjs';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';

const { Content } = Layout;
const { Title, Text } = Typography;

const SeatOccupancy = () => {
    const { currentEventId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [occupancyData, setOccupancyData] = useState([]);
    
    // Layout Modal State
    const [isLayoutModalVisible, setIsLayoutModalVisible] = useState(false);
    const [layoutLoading, setLayoutLoading] = useState(false);
    const [selectedLayoutSession, setSelectedLayoutSession] = useState(null);
    const [seatLayout, setSeatLayout] = useState({});

    useEffect(() => {
        if (currentEventId) {
            fetchData();
        }
    }, [currentEventId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            const docSnap = await getDoc(docRef);
            
            if (!docSnap.exists()) {
                message.error("Event not found");
                setLoading(false);
                return;
            }

            const data = docSnap.data();
            const seatsGenerated = data.sessionsSeatsGenerated || {};
            const orchestraSessions = data.orchestraSessions || [];
            const venues = data.venues || [];

            const sessionsList = [];

            // Add Orchestra Sessions
            orchestraSessions.forEach(os => {
                const sId = `${os.date}_${os.time}`;
                sessionsList.push({
                    key: os.id,
                    type: 'Orchestra',
                    venue: venues.find(v => v.id === os.venue)?.label || os.venue,
                    venueId: os.venue,
                    date: os.date,
                    time: os.time,
                    sessionId: sId,
                    isGenerated: !!seatsGenerated[`${os.venue}_${sId}`] || !!seatsGenerated[sId]
                });
            });

            // Add Performer Sessions
            venues.forEach(venue => {
                if (venue.sessions) {
                    Object.keys(venue.sessions).forEach(dateStr => {
                        const timeslots = venue.sessions[dateStr];
                        timeslots.forEach(timeStr => {
                            const sId = `${dateStr}_${timeStr}`;
                            sessionsList.push({
                                key: `${venue.id}_${sId}`,
                                type: 'Competition',
                                venue: venue.label || venue.id,
                                venueId: venue.id,
                                date: dateStr,
                                time: timeStr,
                                sessionId: sId,
                                isGenerated: !!seatsGenerated[`${venue.id}_${sId}`] || !!seatsGenerated[sId]
                            });
                        });
                    });
                }
            });

            // Fetch Seat Counts for generated sessions
            const seatsCollection = collection(db, `seats${currentEventId || 'APCS2026'}`);
            
            const results = await Promise.all(sessionsList.map(async (session) => {
                if (!session.isGenerated) {
                    return { ...session, total: 0, available: 0, locked: 0, booked: 0 };
                }
                
                try {
                    const qSession = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId));
                    const totalSnapshot = await getCountFromServer(qSession);
                    const total = totalSnapshot.data().count;

                    const qLocked = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId), where('status', '==', 'locked'));
                    const lockedSnapshot = await getDocs(qLocked);
                    let locked = 0;
                    let expiredCount = 0;
                    lockedSnapshot.forEach(docSnap => {
                        const data = docSnap.data();
                        if (data.lockedAt) {
                            const lockedAtTime = data.lockedAt.toDate ? data.lockedAt.toDate().getTime() : data.lockedAt;
                            if (Date.now() - lockedAtTime > 30 * 60 * 1000) {
                                expiredCount++;
                            } else {
                                locked++;
                            }
                        } else {
                            locked++;
                        }
                    });

                    const qAvailable = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId), where('status', '==', 'available'));
                    const availableSnapshot = await getCountFromServer(qAvailable);
                    const available = availableSnapshot.data().count + expiredCount;

                    const qBooked = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId), where('status', 'in', ['booked', 'reserved']));
                    const bookedSnapshot = await getCountFromServer(qBooked);
                    const booked = bookedSnapshot.data().count;

                    return { ...session, total, available, locked, booked };
                } catch (e) {
                    console.error("Failed to fetch seat count for session", session.sessionId, e);
                    return { ...session, total: 0, available: 0, locked: 0, booked: 0 };
                }
            }));

            // Sort by Date and Time
            results.sort((a, b) => {
                if (a.date !== b.date) return a.date.localeCompare(b.date);
                return a.time.localeCompare(b.time);
            });

            setOccupancyData(results);

        } catch (error) {
            console.error("Error fetching occupancy data:", error);
            message.error("Failed to load occupancy data");
        } finally {
            setLoading(false);
        }
    };

    const handleViewLayout = async (session) => {
        setSelectedLayoutSession(session);
        setIsLayoutModalVisible(true);
        setLayoutLoading(true);
        setSeatLayout({});

        try {
            const seatsCollection = collection(db, `seats${currentEventId || 'APCS2026'}`);
            const q = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId));
            const snapshot = await getDocs(q);
            
            const fetchedSeats = [];
            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                if (data.status === 'locked' && data.lockedAt) {
                    const lockedAtTime = data.lockedAt.toDate ? data.lockedAt.toDate().getTime() : data.lockedAt;
                    if (Date.now() - lockedAtTime > 30 * 60 * 1000) {
                        data.status = 'available'; // lock expired
                    }
                }
                fetchedSeats.push({ id: docSnap.id, ...data });
            });

            // Format layout
            const groupedByArea = fetchedSeats.reduce((acc, seat) => {
                const area = seat.areaType;
                if (!acc[area]) acc[area] = [];
                acc[area].push(seat);
                return acc;
            }, {});

            const finalLayouts = {};
            const areaOrder = ['presto', 'allegro', 'lento'];
            const rightSideMax = {
                A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
                G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
                N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
            };

            const areasInDb = Object.keys(groupedByArea);
            const sortedAreas = areasInDb.sort((a, b) => {
                const idxA = areaOrder.indexOf(a.toLowerCase());
                const idxB = areaOrder.indexOf(b.toLowerCase());
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b);
            });

            sortedAreas.forEach(area => {
                if (groupedByArea[area]) {
                    const rowsObject = groupedByArea[area].reduce((acc, seat) => {
                        const row = seat.row;
                        if (!acc[row]) acc[row] = [];
                        acc[row].push(seat);
                        return acc;
                    }, {});

                    const sortedRowKeys = Object.keys(rowsObject).sort((a, b) => a.localeCompare(b));

                    finalLayouts[area] = sortedRowKeys.map(rowKey => {
                        const rowSeats = rowsObject[rowKey];
                        rowSeats.sort((a, b) => b.number - a.number);
                        
                        const mappedRow = rowSeats.map(seat => {
                            let tooltipText = `Status: ${seat.status.toUpperCase()}`;
                            if (seat.status === 'booked' && seat.assignedTo) {
                                tooltipText = `Booked by: ${seat.assignedTo.registrantName || seat.assignedTo.userName} (${seat.assignedTo.userEmail || ''})`;
                            } else if (seat.status === 'locked' && seat.lockedBy) {
                                tooltipText = `Locked by: ${seat.lockedBy.registrantName || seat.lockedBy.userName} (${seat.lockedBy.userEmail || ''})`;
                            } else if (seat.status === 'reserved' && seat.assignedTo) {
                                tooltipText = `Reserved for: ${seat.assignedTo.registrantName || 'N/A'}`;
                            }
                            
                            return {
                                id: seat.id,
                                number: seat.number,
                                row: seat.row,
                                isReserved: seat.status !== 'available',
                                status: seat.status,
                                tooltip: (
                                    <div style={{ textAlign: 'center' }}>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>Seat {seat.seatLabel}</strong>
                                        <span>{tooltipText}</span>
                                    </div>
                                ),
                                statusColor: seat.status === 'booked' ? '#ff4d4f' : seat.status === 'reserved' ? '#bfbfbf' : seat.status === 'locked' ? '#faad14' : undefined
                            };
                        });

                        if (session.venueId === 'Venue1') {
                            const splitSeatNumber = rightSideMax[rowKey];
                            if (splitSeatNumber) {
                                const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
                                if (splitIndex !== -1) {
                                    mappedRow.splice(splitIndex, 0, null, null);
                                }
                            }
                        }

                        return mappedRow;
                    });
                }
            });

            setSeatLayout(finalLayouts);

        } catch (error) {
            console.error("Error loading layout:", error);
            message.error("Failed to load layout details");
        } finally {
            setLayoutLoading(false);
        }
    };

    const columns = [
        {
            title: 'Session Type',
            dataIndex: 'type',
            key: 'type',
            render: text => <Tag color={text === 'Orchestra' ? 'purple' : 'blue'}>{text}</Tag>,
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: text => dayjs(text).format('DD MMM YYYY')
        },
        {
            title: 'Time',
            dataIndex: 'time',
            key: 'time',
        },
        {
            title: 'Generated',
            dataIndex: 'isGenerated',
            key: 'isGenerated',
            render: isGen => isGen ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>
        },
        {
            title: 'Total Seats',
            dataIndex: 'total',
            key: 'total',
        },
        {
            title: 'Available',
            dataIndex: 'available',
            key: 'available',
            render: val => <Text type="success" strong>{val}</Text>
        },
        {
            title: 'Locked',
            dataIndex: 'locked',
            key: 'locked',
            render: val => <Text type="warning" strong>{val}</Text>
        },
        {
            title: 'Booked',
            dataIndex: 'booked',
            key: 'booked',
            render: val => <Text type="danger" strong>{val}</Text>
        },
        {
            title: 'Occupancy Rate',
            key: 'occupancy',
            render: (_, record) => {
                if (!record.total) return <Progress percent={0} size="small" />;
                const percent = Math.round((record.booked / record.total) * 100);
                return <Progress percent={percent} size="small" status={percent === 100 ? "success" : "active"} />;
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button 
                    type="primary" 
                    size="small" 
                    icon={<EyeOutlined />} 
                    disabled={!record.isGenerated}
                    onClick={() => handleViewLayout(record)}
                >
                    View Layout
                </Button>
            )
        }
    ];

    return (
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>Seat Occupancy Overview</Title>
                    <Text type="secondary">
                        View real-time seat statistics for all orchestra and competition sessions.
                    </Text>
                </div>
                <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={fetchData} 
                    loading={loading}
                >
                    Refresh Data
                </Button>
            </div>

            <Table 
                columns={columns} 
                dataSource={occupancyData} 
                loading={loading}
                pagination={{ pageSize: 20 }}
                bordered
            />

            <Modal
                title={`Seat Layout: ${selectedLayoutSession?.venue} - ${selectedLayoutSession?.date ? dayjs(selectedLayoutSession.date).format('DD MMM YYYY') : ''} ${selectedLayoutSession?.time}`}
                open={isLayoutModalVisible}
                onCancel={() => setIsLayoutModalVisible(false)}
                footer={null}
                width={1200}
                destroyOnClose
            >
                {layoutLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>Loading seat layout...</div>
                    </div>
                ) : (
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px 0' }}>
                        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'center' }}>
                            <Tag color="default">Available</Tag>
                            <Tag color="orange">Locked (In Checkout)</Tag>
                            <Tag color="red">Booked</Tag>
                            <Tag color="default" style={{ background: '#bfbfbf' }}>Reserved</Tag>
                        </Space>
                        {Object.keys(seatLayout).map(areaType => {
                            const layoutForPicker = seatLayout[areaType] || [];
                            if (layoutForPicker.length === 0) return null;
                            return (
                                <div key={areaType} style={{ marginBottom: '40px' }}>
                                    <Title level={4} style={{ textAlign: 'center', textTransform: 'capitalize' }}>
                                        Tier: {areaType}
                                    </Title>
                                    <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
                                        <CustomSeatPicker
                                            layout={layoutForPicker}
                                            selectedSeats={[]}
                                            onSelect={() => {}}
                                            onDeselect={() => {}}
                                            maxSeats={0}
                                            isReadOnly={true}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal>
        </Content>
    );
};

export default SeatOccupancy;
